import type {
  MembershipOption,
  MembershipOptionsV2,
  MembershipPricingOption,
  Prices,
  PriceWithMembershipOptions,
  PricingOption,
  PricingToggle,
  Maybe,
  RelatedVariant,
  ProductPricingDetailsType,
  Price
} from "@shared/graphql.types";
import { useStrikethrough } from "@shared/ui.hooks";
import { useCallback, useState, forwardRef, useRef, useEffect } from "react";
import { ProductATB, OutOfStock } from "@growth/page.products";
import { AccordionWithSingleMembershipOption } from "basket_and_checkout/shared/components/PDP/Pricing/AccordionWithSingleMembershipOption";
import { MembershipOptionsModal } from "packages/core-shopping/components/PDP/MembershipOptionsModal/MembershipOptionsModal";
import * as styles from "./PricingOptions.css";
import { useUserV2 } from "hooks/useUserV2";
import { dispatch } from "@shared/utils.events";
import { RelatedVariants } from "../RelatedVariants/RelatedVariants";
import { useCurrencySymbol } from "hooks";
import { useExperimentViewed } from "tracking/hooks/useExperimentViewed";
import { PAGE_EXPERIMENT_PDP_NO_DEFAULT } from "@shared/utils.experimentation";
import {
  atbWrapperVariant,
  membershipOptionsInView,
  shouldRenderCTADisplayPrice,
  shouldSetDefaultMembershipOption,
  getPricingSelectionFromBasket,
  findPriceOptionByPricingSelection
} from "./noDefaultExperiment.utils";

type Props = {
  membershipOptions: MembershipOptionsV2[];
  pricingOptions: PricingToggle;
  variant: { sku: string; prices: Prices };
  isInWishlist: boolean;
  handleWishlist: () => void;
  onAddToBag: ({
    membershipSKU,
    locationTypeDetails
  }: {
    membershipSKU: string;
    locationTypeDetails: string;
  }) => void;
  context?: string;
  relatedVariants?: Maybe<RelatedVariant[]>;
  selectedVariantSlug?: string;
  isOutOfStock?: boolean;
  handleSubmit: ({ email }: { email: string }) => Promise<boolean>;
  submitted: boolean;
  membershipInBag: boolean;
  isMember: boolean;
  isFreeTrialVariant: boolean;
  onPriceChange?: ((price: PricingOption) => void) | null;
  noDefaultPageExperimentVariant: number;
  isShadeMatchingEnabled?: boolean | null;
  hasMembership?: boolean;
};

const getDefaultMembershipOption = (
  price?: PricingOption
): MembershipOption | undefined => {
  if (!price) return;
  if ("memberships" in price) {
    return (price as PriceWithMembershipOptions).memberships.find(
      (membership) => membership.isDefault
    );
  }
  if ("membership" in price) {
    return (price as MembershipPricingOption).membership;
  }
};
const trackingDetailsMap = {
  1: "Guest Price",
  2: "Members Price"
};

const getCustomPrice = (
  promoPrice: ProductPricingDetailsType | null,
  regularPrice: ProductPricingDetailsType | null,
  showMembershipOptions: boolean,
  selectedPrice: Price | undefined
) => {
  if (promoPrice?.formatted) {
    return promoPrice?.formatted || regularPrice?.formatted || undefined;
  }

  if (showMembershipOptions) {
    return selectedPrice?.price.priceText;
  }

  return undefined;
};

export const PricingOptionsExperiment2 = forwardRef<HTMLDivElement, Props>(
  (
    {
      membershipOptions,
      pricingOptions,
      variant,
      onAddToBag,
      handleWishlist,
      isInWishlist,
      context,
      relatedVariants = [],
      selectedVariantSlug,
      isOutOfStock = false,
      handleSubmit,
      submitted,
      membershipInBag,
      isMember,
      onPriceChange = null,
      noDefaultPageExperimentVariant,
      isShadeMatchingEnabled,
      hasMembership = false
    },
    ref
  ) => {
    const trackExperimentViewed = useExperimentViewed();

    useEffect(() => {
      trackExperimentViewed(
        PAGE_EXPERIMENT_PDP_NO_DEFAULT.name,
        noDefaultPageExperimentVariant
      );
    }, [noDefaultPageExperimentVariant, trackExperimentViewed]);

    const membershipOptionsRef = useRef<HTMLDivElement>(null);
    const user = useUserV2();
    const { hasGiftMembershipInBag } = user;
    const prices = pricingOptions.data.prices;
    const productPrices = pricingOptions.data.productPrices;
    const currencySymbol = useCurrencySymbol();

    const defaultMembershipSku =
      membershipOptions?.find((membershipOption) => membershipOption.isDefault)
        ?.sku ?? membershipOptions?.[0]?.sku;

    // Determine default price based on basket contents first, then experiment variant
    const pricingSelectionFromBasket = getPricingSelectionFromBasket(
      user.basket
    );

    const basketBasedDefault = findPriceOptionByPricingSelection(
      prices,
      pricingSelectionFromBasket
    );

    const experimentBasedDefault = shouldSetDefaultMembershipOption(
      noDefaultPageExperimentVariant
    )
      ? prices.find(
          (priceOption) => priceOption.id === pricingOptions.data.defaultPrice
        )
      : undefined;

    const defaultPrice = basketBasedDefault ?? experimentBasedDefault;

    const [selectedPrice, setSelectedPrice] = useState(defaultPrice);

    // Update selectedPrice when defaultPrice changes (e.g., when navigating to a new product or basket updates)
    useEffect(() => {
      setSelectedPrice(defaultPrice);
    }, [defaultPrice]);

    // Derive selectedMembershipSKU from selectedPrice to avoid state synchronization issues
    const selectedMembershipSKU = getDefaultMembershipOption(
      selectedPrice?.price
    )?.sku;

    const [isModalOpen, setIsModalOpen] = useState<string | null>(null);

    const onSelectMembership = async (
      chosenMembership: MembershipOptionsV2
    ) => {
      await onAddToBag?.({
        membershipSKU: chosenMembership.sku,
        locationTypeDetails: "membership_selection_panel"
      });
      setIsModalOpen(null);
    };

    const {
      promoPrice,
      price: regularPrice,
      setActiveUserType
    } = useStrikethrough(
      productPrices ?? undefined,
      hasMembership || selectedPrice?.id === "2"
    );

    const onAccordionChange = useCallback(
      (value: string) => {
        dispatch("page element clicked", {
          target: "PDP - Pricing Options",
          details: `${trackingDetailsMap[value]} selected`,
          location: "PDP - Core Product Info"
        });
        const option = prices.find((priceOption) => priceOption.id === value);

        if (!option) return;

        setSelectedPrice(option);

        if (onPriceChange) {
          const userType = (option.price as MembershipPricingOption).membership
            ? "member"
            : "guest";
          setActiveUserType(userType);
          onPriceChange(option.price);
        }
      },
      [prices, onPriceChange, setActiveUserType]
    );

    const showMembershipOptions =
      !isMember && !membershipInBag && !hasGiftMembershipInBag;

    const customPrice = getCustomPrice(
      promoPrice ?? null,
      regularPrice ?? null,
      showMembershipOptions,
      selectedPrice
    );
    const safeRelatedVariants = relatedVariants ?? [];
    const hasMultipleVariants = safeRelatedVariants.length > 1;
    const membershipSelected = !!selectedPrice;

    return (
      <section data-testid="og_pdp_panel">
        {isOutOfStock ? (
          <div className={styles.relatedVariantContainer}>
            <hr className={styles.oosLineBreak} />

            {hasMultipleVariants && (
              <>
                <RelatedVariants
                  relatedVariants={relatedVariants ?? []}
                  selectedVariantSlug={selectedVariantSlug || ""}
                  title="Shade Options"
                  isShadeMatchingEnabled={isShadeMatchingEnabled}
                  currentSku={variant.sku}
                />
              </>
            )}

            <OutOfStock
              ref={ref}
              handleSubmit={handleSubmit}
              submitted={submitted}
            />
          </div>
        ) : (
          <>
            {showMembershipOptions && (
              <AccordionWithSingleMembershipOption
                {...{
                  selectedValue: selectedPrice?.id,
                  onAccordionChange,
                  prices: pricingOptions.data.prices || [],
                  productPrices: productPrices ?? undefined,
                  defaultMembershipSku,
                  setShowMembershipOptions: setIsModalOpen,
                  context
                }}
              />
            )}
            <div className={styles.relatedVariantContainer}>
              {hasMultipleVariants && (
                <div>
                  <hr className={styles.inStockLineBreak} />
                  <RelatedVariants
                    relatedVariants={relatedVariants ?? []}
                    selectedVariantSlug={selectedVariantSlug || ""}
                    title="Shade Options"
                    isShadeMatchingEnabled={isShadeMatchingEnabled}
                    currentSku={variant.sku}
                  />
                </div>
              )}

              <div
                className={
                  styles.productATBWrapper[
                    atbWrapperVariant(noDefaultPageExperimentVariant)
                  ]
                }
              >
                <ProductATB
                  isInWishlist={isInWishlist}
                  prices={variant.prices}
                  customPrice={customPrice}
                  handleWishlist={handleWishlist}
                  handleAddToBag={() => {
                    if (!selectedPrice && showMembershipOptions) {
                      const ctaIsSticky =
                        atbWrapperVariant(noDefaultPageExperimentVariant) ===
                          "adaptive" &&
                        document.documentElement.clientWidth < 1024;

                      if (
                        ctaIsSticky &&
                        membershipOptionsInView(membershipOptionsRef) === false
                      ) {
                        membershipOptionsRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "center"
                        });
                      }
                      return;
                    }

                    onAddToBag({
                      membershipSKU: selectedMembershipSKU || "",
                      locationTypeDetails: "pdp-header"
                    });
                  }}
                  context="desktop"
                  ref={ref}
                  isMember={isMember}
                  membershipInBag={membershipInBag}
                  currencySymbol={currencySymbol}
                  renderDisplayPrice={shouldRenderCTADisplayPrice(
                    noDefaultPageExperimentVariant,
                    membershipSelected
                  )}
                />
              </div>
            </div>
          </>
        )}

        {selectedMembershipSKU && (
          <MembershipOptionsModal
            isOpen={!!isModalOpen}
            onClose={() => setIsModalOpen(null)}
            onSelect={onSelectMembership}
            initialMembershipSku={selectedMembershipSKU}
            memberships={membershipOptions}
            defaultMembershipSku={defaultMembershipSku}
          />
        )}
      </section>
    );
  }
);
